import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { registerServiceWorker } from './pwa/registerServiceWorker';
import { createAppStore, getPersistor, ApiUrls } from '@ideoz/core';
import { default as apiClient } from '@ideoz/core/api/client';
import { initAnalytics } from '@ideoz/core/analytics';

import App from './App';
import './index.scss';
// Mixpanel disabled — all calls commented out below. Re-enable by uncommenting
// this import, the init block, and the store.subscribe identity-sync block.
// import mixpanel from "mixpanel-browser";
import { FlowsProvider } from '@flows/react';
import { components, tourComponents } from './flows-components';
import { Link } from './components';
import { useSelector } from 'react-redux';

/** `@babel/types` (via workbench hot-edit JSX patcher, dynamically imported) expects Node `process` in some builds. */
if (!(globalThis as unknown as { process?: unknown }).process) {
  (globalThis as unknown as { process: { env: { NODE_ENV: string } } }).process = {
    env: { NODE_ENV: import.meta.env.PROD ? 'production' : 'development' },
  };
}

// const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;
const FLOWS_ENVIRONMENT =
  (import.meta.env.VITE_NODE_ENV as string | undefined) ?? import.meta.env.MODE;

// Mixpanel init disabled.
// try {
//   mixpanel.init(MIXPANEL_TOKEN, {
//     autocapture: true,
//     track_pageview: true,
//     record_sessions_percent: 100,
//     record_heatmap_data: true,
//     record_mask_all_text: false,
//     ignore_dnt: true,
//   });
// } catch (e) {
//   console.warn('Mixpanel initialization failed:', e);
// }

// Production-only: Google Tag Manager and Meta Pixel — injected after React renders
function ProductionAnalytics() {
  useEffect(() => {
    const GTM_ID = import.meta.env.VITE_GTM_ID as string;
    const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string;

    try {
      // Google Tag Manager (GA4 should be configured as a tag inside the GTM container)
      if (GTM_ID && /^GTM-[A-Z0-9]+$/i.test(GTM_ID)) {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
        document.head.appendChild(gtmScript);
      }

      // Meta Pixel Code (PageView tracked via App.tsx on each route change)
      if (META_PIXEL_ID && /^\d+$/.test(META_PIXEL_ID)) {
        const metaPixel = document.createElement('script');
        metaPixel.textContent = `!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init','${META_PIXEL_ID}');`;
        document.head.appendChild(metaPixel);
      }
    } catch (e) {
      console.warn('Production analytics tags initialization failed:', e);
    }
  }, []);

  return null;
}

// Create Redux store
const store = createAppStore();
const persistor = getPersistor(store);

// Initialize analytics service
initAnalytics(apiClient, ApiUrls.events, () => store.getState() as any);

// Register the PWA service worker so the app is installable ("Install app").
registerServiceWorker();

// Mixpanel identity-sync disabled.
// let previousIsLoggedIn: boolean | null = null;
// store.subscribe(() => {
//   try {
//     const state = store.getState() as any;
//     const isLoggedIn: boolean = state.app?.isLoggedIn ?? false;
//     const user = state.app?.user;
//     const isSierajUser = user?.email?.includes('@sieraj.com');
//
//     if (isLoggedIn === previousIsLoggedIn) return;
//     previousIsLoggedIn = isLoggedIn;
//
//     if (isSierajUser) {
//       mixpanel.opt_out_tracking();
//       return;
//     }
//
//     if (isLoggedIn && user?.id) {
//       mixpanel.opt_in_tracking();
//       mixpanel.identify(String(user.id));
//       mixpanel.people.set({
//         $user_id: user.id,
//         $name: user.name ?? '',
//         $email: user.email ?? '',
//         $avatar: user.avatar ?? '',
//         provider: user.provider ?? '',
//       });
//     } else if (!isLoggedIn) {
//       mixpanel.reset();
//     }
//   } catch (e) {
//     // Non-blocking — never let this break the app
//   }
// });

// Wrapper to provide live user data to FlowsProvider, with exclusion for @sieraj.com emails
function FlowsProviderWrapper({ children }: { children: React.ReactNode }) {
  const user = useSelector((state: any) => state.app?.user);
  const isSierajUser = user?.email?.includes('@sieraj.com');
  if (isSierajUser) {
    return <>{children}</>;
  }
  return (
    <FlowsProvider
      organizationId="4a95b56e-0f12-41bb-bd1d-c23add965dc3"
      userId={user?.id || ''}
      userProperties={{
        email: user?.email || '',
        name: user?.name || '',
      }}
      environment={FLOWS_ENVIRONMENT}
      components={{ ...components }}
      tourComponents={{ ...tourComponents }}
      LinkComponent={Link}
    >
      {children}
    </FlowsProvider>
  );
}

const rootElement = document.getElementById('root');

if (rootElement) {
  if (!(rootElement as any)._reactRoot) {
    (rootElement as any)._reactRoot = ReactDOM.createRoot(rootElement);
  }
  (rootElement as any)._reactRoot.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.MODE === 'production' ? (
              <FlowsProviderWrapper>
                <ProductionAnalytics />
                <App />
              </FlowsProviderWrapper>
            ) : (
              <App />
            )}
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
}
